// Portfolio Administration v1.0 PR 6 §11.5 — PSA Loan Tape CSV export
// modal. Surfaces pre-export validation warnings (unresolved variances,
// unlocked entries, empty PSA Exhibit B blobs) before downloading the
// 59-field CSV.

import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { loanTapePeriodApi } from "@/api/portfolioAdmin/pr6Client";
import type { LoanTapeEntryDto } from "@/api/portfolioAdmin/pr6Types";

interface PsaLoanTapeExportModalProps {
  open: boolean;
  period: string;
  entries: LoanTapeEntryDto[];
  onClose: () => void;
}

export function PsaLoanTapeExportModal({
  open,
  period,
  entries,
  onClose,
}: PsaLoanTapeExportModalProps) {
  const unlocked = entries.filter(
    (e) => e.status !== "locked" && e.status !== "distributed",
  ).length;
  const variancesOpen = entries.filter(
    (e) => e.varianceFlag && (e.varianceReason ?? "").trim() === "",
  ).length;
  const url = loanTapePeriodApi.exportCsvUrl(period);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Export PSA Loan Tape — ${period}`}
      description="Generates a CSV with the operational columns plus the 59 PSA Exhibit B fields. Verify the warnings below before downloading."
      footer={
        <>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={onClose}
            data-testid="psa-export-cancel"
          >
            Cancel
          </Button>
          <a
            href={url}
            download={`psa-loan-tape-${period}.csv`}
            className="inline-flex items-center justify-center gap-space-2 font-display font-bold uppercase bg-gold text-onyx hover:bg-gold-highlight h-8 px-space-3 text-label tracking-button rounded-sm"
            data-testid="psa-export-download"
            onClick={onClose}
          >
            Download CSV
          </a>
        </>
      }
    >
      <div className="flex flex-col gap-space-2 text-small">
        <p className="m-0">
          <strong>{entries.length}</strong>{" "}
          {entries.length === 1 ? "entry" : "entries"} in scope for {period}.
        </p>
        {unlocked > 0 && (
          <p
            className="m-0 text-status-warning"
            data-testid="psa-export-unlocked-warning"
          >
            ⚠ {unlocked} {unlocked === 1 ? "entry is" : "entries are"} not yet
            locked — the CSV will include them but downstream PSA consumers
            may reject pre-lock rows.
          </p>
        )}
        {variancesOpen > 0 && (
          <p
            className="m-0 text-status-warning"
            data-testid="psa-export-variance-warning"
          >
            ⚠ {variancesOpen}{" "}
            {variancesOpen === 1 ? "entry has" : "entries have"} an unresolved
            variance flag.
          </p>
        )}
        <p className="m-0 text-caption text-gray-mid">
          PSA Exhibit B field-level shape: 59 columns are written from each
          entry's <code>PsaExhibitBJson</code> blob (spec §11.2). Empty
          fields render as blanks.
        </p>
      </div>
    </Modal>
  );
}
